/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { ErrorCode } from '../../modules/lxs-create-order/lxs-create-order.data';
import { ensureAuthenticated } from '../../utilities/is-authenticated';
import { trackedAction } from '../../utilities/track-execution-time';
import { escapeODataStringLiteral } from '../../utilities/odata';

/**
 * GetCart Input Action
 */
export class GetCartInput implements Msdyn365.IActionInput {
    public getCacheKey = () => 'GetCart';
    public getCacheObjectType = () => 'GetCart';
    public dataCacheType = (): Msdyn365.CacheType => 'none';
}

export interface IGetCart {
    result: IGetCartData;
    error?: ErrorCode;
}

export interface IGetCartData {
    '@odata.context'?: string;
    CustomerAccountNumber: string;
    AdditionalInfo: string;
    CustomerPo: string;
    SalesOrg: string;
    SalesOrgText: string;
    Step: number;
    OrderType: string;
    DistributionChannel: string;
    ShipToId: string;
    ShipTo: IShipToTableData;
    SoldToId: string;
    SoldTo: ISoldToTableData;
    Division: string;
    DivisionText: string;
    Incoterm1: string;
    Incoterm2: string;
    RecordCreatedDate: Date;
    Items: IGetCartItems[];
    ExtensionProperties: unknown[];
}
export interface IGetCartItems {
    CustomerAccountNumber: string;
    Number: number;
    ProductNumber: string;
    ProductName: string;
    CustomerProductNumber: string;
    RequestedDate: Date;
    UnitOfMeasure: string;
    EndCustomerNumber: string;
    EndCustomerName: string;
    ExtensionProperties: unknown[];
    PriceUnit?: string;
    PriceFactor?: string;
    PriceAmount?: string;
    PriceCurrency?: string;
    MissingParameters?: string;
    PriceMissing?: string;
}

export interface IShipToTableData {
    AddressId: string;
    Name1: string;
    Name2: string;
    VatId: string;
    Street1: string;
    HouseNumber: string;
    Street4: string;
    Street5: string;
    PostlCode: string;
    City: string;
    Country: string;
    Phone: string;
    Mail: string;
    ShortAddress1: string;
    ShortAddress2: string;
    ShortAddress3: string;
    ShortAddress4: string;
    ShortAddress5: string;
    ShortAddress6: string;
    ShortAddress7: string;
    ShortAddress8: string;
    ShortAddress9: string;
    ShortAddress10: string;
    RecordCreatedDate: string;
    SalesAreas: ISalesArea[];
    LocalAddress: ILocalAddress;
    ExtensionProperties: [];
}

export interface ILocalAddress {
    ShortAddress1: string;
    ShortAddress2: string;
    ShortAddress3: string;
    ShortAddress4: string;
    ShortAddress5: string;
    ShortAddress6: string;
    ShortAddress7: string;
    ShortAddress8: string;
    ShortAddress9: string;
    ShortAddress10: string;
    RecordCreatedDate: string;
    ExtensionProperties: [];
}

export interface ISoldToTableData {
    AddressId: string;
    Name1: string;
    Name2: string;
    VatId: string;
    Street1: string;
    HouseNumber: string;
    Street4: string;
    Street5: string;
    PostlCode: string;
    City: string;
    Country: string;
    Phone: string;
    Mail: string;
    ShortAddress1: string;
    ShortAddress2: string;
    ShortAddress3: string;
    ShortAddress4: string;
    ShortAddress5: string;
    ShortAddress6: string;
    ShortAddress7: string;
    ShortAddress8: string;
    ShortAddress9: string;
    ShortAddress10: string;
    RecordCreatedDate: string;
    SalesAreas: ISalesArea[];
    LocalAddress: ILocalAddress;
    ExtensionProperties: [];
}

interface ISalesArea {
    SalesOrg: string;
    SalesOrgText: string;
    DistributionChannel: string;
    DistributionChannelText: string;
    Division: string;
    DivisionText: string;
    OrderCreate: boolean;
    RecordCreatedDate: Date;
    ExtensionProperties: [];
}

const createInput = (_: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    return new GetCartInput();
};

async function action(_: GetCartInput, ctx: Msdyn365.IActionContext): Promise<IGetCart> {
    ensureAuthenticated(ctx);
    const response = await Msdyn365.sendRequest<IGetCart>(
        `${ctx.requestContext.apiSettings.baseUrl}Commerce/GetCart(language='${escapeODataStringLiteral(ctx.requestContext.locale)}')`,
        'get',
        {},
        {
            headers: {
                OUN: ctx.requestContext.apiSettings.oun,
                Authorization: `id_token ${ctx.requestContext.user.token}`
            }
        }
    );
    if (response.status === 200 && response.data) {
        return { ...response.data };
    } else {
        return { result: {} as IGetCartData, error: { errorCode: response.status, errorText: response.statusText } };
    }
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IGetCart>>((input, ctx) => trackedAction(action, input, ctx, 'GetCart')),
    id: 'GetCart',
    input: createInput
});
